import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { adminAnalyticsService } from "@/services/admin-analytics-service";
import { ApiError } from "@/services/BaseService";
import {
  AdminAnalyticsPreferencesBlob,
  AdminAnalyticsPreferencesResponseDto,
  PlatformAnalyticsExportFilters,
  PlatformAnalyticsRefreshRunDto,
  PlatformAnalyticsSummaryFilters,
  PlatformAnalyticsSummaryResponseDto,
  ReportScheduleConfigDto,
  TriggerAnalyticsRefreshRequest,
  UpdateReportScheduleRequest,
} from "@/types/admin-analytics";

const FIVE_MINUTES = 5 * 60 * 1000;

/** Poll cadence while a refresh run is RUNNING (spec suggests 3–5s). */
export const ANALYTICS_REFRESH_POLL_INTERVAL_MS = 4000;

export const adminAnalyticsKeys = {
  all: ["admin", "analytics"] as const,
  summaries: () => [...adminAnalyticsKeys.all, "summary"] as const,
  summary: (filters: PlatformAnalyticsSummaryFilters) =>
    [...adminAnalyticsKeys.summaries(), { filters }] as const,
  refreshStatus: () => [...adminAnalyticsKeys.all, "refresh-status"] as const,
  preferences: () => [...adminAnalyticsKeys.all, "preferences"] as const,
  reportSchedules: () =>
    [...adminAnalyticsKeys.all, "report-schedule"] as const,
};

export const usePlatformAnalyticsSummary = (
  filters: PlatformAnalyticsSummaryFilters,
) => {
  return useQuery<PlatformAnalyticsSummaryResponseDto>({
    queryKey: adminAnalyticsKeys.summary(filters),
    queryFn: () => adminAnalyticsService.getSummary(filters),
    staleTime: FIVE_MINUTES,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Latest refresh run (no runId — the endpoint returns the most recent run,
 * or `null` when nothing ever ran). Polls automatically while RUNNING.
 */
export const useAnalyticsRefreshStatus = () => {
  return useQuery<PlatformAnalyticsRefreshRunDto | null>({
    queryKey: adminAnalyticsKeys.refreshStatus(),
    queryFn: () => adminAnalyticsService.getRefreshStatus(),
    staleTime: 30 * 1000,
    refetchInterval: (query) =>
      query.state.data?.status === "RUNNING"
        ? ANALYTICS_REFRESH_POLL_INTERVAL_MS
        : false,
  });
};

export const useTriggerAnalyticsRefresh = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TriggerAnalyticsRefreshRequest) =>
      adminAnalyticsService.triggerRefresh(body),
    onSuccess: (run) => {
      // 202: the returned run is already RUNNING — seed the status cache so polling starts.
      queryClient.setQueryData(adminAnalyticsKeys.refreshStatus(), run);
    },
    onError: (error) => {
      // 409: a refresh is already running — attach to the in-flight run.
      if (error instanceof ApiError && error.statusCode === 409) {
        toast.error(t("analytics.platform.refreshConflict"));
        queryClient.invalidateQueries({
          queryKey: adminAnalyticsKeys.refreshStatus(),
        });
        return;
      }
      toast.error(
        error instanceof Error
          ? error.message
          : t("analytics.platform.status.failed"),
      );
    },
  });
};

export const useExportPlatformAnalyticsCsv = () => {
  return useMutation({
    mutationFn: (filters: PlatformAnalyticsExportFilters) =>
      adminAnalyticsService.exportCsv(filters),
  });
};

export const useAnalyticsPreferences = () => {
  return useQuery<AdminAnalyticsPreferencesResponseDto>({
    queryKey: adminAnalyticsKeys.preferences(),
    queryFn: () => adminAnalyticsService.getPreferences(),
    staleTime: FIVE_MINUTES,
  });
};

export const useUpdateAnalyticsPreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: AdminAnalyticsPreferencesBlob) =>
      adminAnalyticsService.updatePreferences(preferences),
    onMutate: async (preferences) => {
      // Optimistic apply — backend stores the blob verbatim and echoes it back.
      await queryClient.cancelQueries({
        queryKey: adminAnalyticsKeys.preferences(),
      });
      const previous =
        queryClient.getQueryData<AdminAnalyticsPreferencesResponseDto>(
          adminAnalyticsKeys.preferences(),
        );
      queryClient.setQueryData(adminAnalyticsKeys.preferences(), {
        preferences,
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          adminAnalyticsKeys.preferences(),
          context.previous,
        );
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(adminAnalyticsKeys.preferences(), data);
    },
  });
};

export const useReportSchedules = () => {
  return useQuery<ReportScheduleConfigDto[]>({
    queryKey: adminAnalyticsKeys.reportSchedules(),
    queryFn: () => adminAnalyticsService.getReportSchedules(),
    staleTime: FIVE_MINUTES,
  });
};

export const useUpdateReportSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: UpdateReportScheduleRequest) =>
      adminAnalyticsService.updateReportSchedule(config),
    onSuccess: (saved) => {
      // PUT replaces one period's config — merge the echoed row into the cached list.
      queryClient.setQueryData<ReportScheduleConfigDto[]>(
        adminAnalyticsKeys.reportSchedules(),
        (previous) =>
          previous?.map((row) => (row.period === saved.period ? saved : row)),
      );
    },
  });
};
